// src/account/token.ts
import z from "zod";
var ClientAccessTokenSchema = z.object({
  expiresAt: z.string(),
  expiresAtMs: z.number(),
  expiresIn: z.string(),
  expiresInMs: z.number(),
  token: z.string()
});
var ClientTokenPairSchema = z.object({
  refreshToken: z.string(),
  accessToken: ClientAccessTokenSchema
});
var LoginResponseSchema = z.object({
  tokenPair: ClientTokenPairSchema
});
var LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string()
});
var AdminLoginRequestSchema = LoginRequestSchema.extend({});
var AdminLoginResponseSchema = LoginResponseSchema.extend({});
var RefreshTokenRequestSchema = z.object({
  accessToken: z.string()
});

// src/account/user.ts
import z3 from "zod";

// src/objectId.ts
import z2 from "zod";
var checkForHexRegExp = new RegExp("^[0-9a-fA-F]{24}$");
function isObjectID(id) {
  if (id == null)
    return false;
  if (typeof id === "number") {
    return true;
  }
  if (typeof id === "string") {
    return id.length === 12 || id.length === 24 && checkForHexRegExp.test(id);
  }
  return false;
}
var ObjectIdSchema = z2.string().superRefine((val, ctx) => {
  if (!isObjectID(val)) {
    ctx.addIssue({
      code: z2.ZodIssueCode.invalid_type,
      // @ts-ignore
      expected: "ObjectId",
      // @ts-ignore
      received: val,
      message: `Expected ObjectId, received ${val}`
    });
  }
});

// src/account/user.ts
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["Contractor"] = "contractor";
  UserRole2["Employee"] = "employee";
  UserRole2["Admin"] = "admin";
  return UserRole2;
})(UserRole || {});
var SignUpRequestSchema = z3.object({
  role: z3.nativeEnum(UserRole),
  password: z3.string().min(8),
  code: z3.string().min(5).max(5),
  company: z3.object({
    name: z3.string().min(1),
    email: z3.string().email()
  }).optional(),
  user: z3.object({
    email: z3.string().email(),
    firstName: z3.string().min(1),
    lastName: z3.string().min(1)
  }).optional()
}).superRefine((schema, ctx) => {
  const isAdmin = schema.role === "admin" /* Admin */;
  if (isAdmin) {
    ctx.addIssue({
      code: z3.ZodIssueCode.invalid_enum_value,
      message: "Cannot create admin user",
      received: schema.role,
      options: ["contractor" /* Contractor */, "employee" /* Employee */]
    });
    return;
  }
  const hasUser = Boolean(schema.user);
  const hasCompany = Boolean(schema.company);
  const isContractor = schema.role === "contractor" /* Contractor */;
  if (isContractor && !hasCompany) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Contractor must have a company"
    });
    return;
  }
  if (!isContractor && !hasUser) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Employee must have a user"
    });
    return;
  }
});
var UserSchema = z3.object({
  id: ObjectIdSchema,
  email: z3.string().email(),
  role: z3.nativeEnum(UserRole),
  firstName: z3.string(),
  lastName: z3.string()
});
var ChangePasswordSchema = z3.object({
  newPassword: z3.string().min(8),
  password: z3.string().min(8)
});
var RequestPasswordRecoveryCodeRequestSchema = z3.object({
  email: z3.string().email()
});
var ChangePasswordRecoveryCodeRequestSchema = z3.object({
  email: z3.string().email(),
  code: z3.string().min(5).max(5),
  newPassword: z3.string().min(8)
});
var CodeType = /* @__PURE__ */ ((CodeType2) => {
  CodeType2["SignUp"] = "SignUp";
  CodeType2["ForgotPassword"] = "ForgotPassword";
  return CodeType2;
})(CodeType || {});
var VerifyPasswordRecoveryCodeRequestSchema = z3.object({
  email: z3.string().email(),
  code: z3.string().min(5).max(5),
  type: z3.nativeEnum(CodeType)
});
var ChangePasswordRecoveryRequestSchema = z3.object({
  recoveryToken: z3.string(),
  newPassword: z3.string().min(8)
});
var RequestSignUpCodeRequestSchema = z3.object({
  email: z3.string().email(),
  name: z3.string().min(1).optional()
});
var SignUpResponseSchema = LoginResponseSchema;

// src/account/employee.ts
import z12 from "zod";

// src/address.ts
import z4 from "zod";
var CoordinateSchema = z4.object({
  lat: z4.number(),
  lng: z4.number()
});
var AddressSchema = CoordinateSchema.extend({
  text: z4.string(),
  city: z4.string(),
  state: z4.string(),
  country: z4.string(),
  postalCode: z4.string()
});

// src/core/mobileNumber.ts
import { z as z5 } from "zod";
var format = (mask, number) => {
  let s = "" + number;
  let r = "";
  for (let im = 0, is = 0; im < mask.length && is < s.length; im++) {
    r += mask.charAt(im) === "X" ? s.charAt(is++) : mask.charAt(im);
  }
  return r;
};
var clean = (number) => number.replace(/(-|\s)/g, "");
var regex = /^\d+$/;
var MobileNumberSchema = z5.coerce.string().superRefine((val, ctx) => {
  const number = clean(val);
  if (!regex.test(number) || number.length !== 10) {
    ctx.addIssue({
      code: z5.ZodIssueCode.custom,
      message: "Phone number should be a valid USA number"
    });
  }
}).transform((val) => {
  const number = clean(val);
  return format("XXX-XXX-XXXX", number);
});

// src/filesystem/file.ts
import z6 from "zod";
var UploadFieldsSchema = z6.object({
  key: z6.string(),
  value: z6.string()
});
var GetUploadResponseSchema = z6.object({
  url: z6.string().url(),
  fields: z6.array(UploadFieldsSchema)
});
var ImageSchema = z6.object({
  small: z6.string().url(),
  medium: z6.string().url(),
  large: z6.string().url()
});
var FileState = /* @__PURE__ */ ((FileState2) => {
  FileState2["IN_PROGRESS"] = "IN_PROGRESS";
  FileState2["COMPLETED"] = "COMPLETED";
  return FileState2;
})(FileState || {});
var CreateFileRequestSchema = z6.object({
  name: z6.string(),
  contentType: z6.string(),
  contentLength: z6.number()
});
var FileSchema = CreateFileRequestSchema.extend({
  id: z6.string(),
  url: z6.string().url().optional()
});
var GetFileRequestSchema = z6.object({
  id: ObjectIdSchema
});
var GetFileResponseSchema = z6.object({
  file: FileSchema
});
var CreateFileResponseSchema = GetUploadResponseSchema.extend({
  file: FileSchema
});

// src/jobs/job.ts
import { isBefore } from "date-fns";
import z11 from "zod";

// src/boolean.ts
import { z as z7 } from "zod";
var StringBooleanSchema = z7.enum(["true", "false"]);

// src/pagination/pagination.ts
import z8 from "zod";
function withPaginationResponse(schema) {
  return z8.object({
    nextOffset: z8.string().optional(),
    cursor: z8.string().optional(),
    items: z8.array(schema)
  });
}
var PaginationRequestSchema = z8.object({
  cursor: z8.string().optional(),
  totalItemsPerPage: z8.coerce.number().max(100),
  offset: z8.string().optional()
});
var PageParamsSchema = z8.object({
  page: z8.coerce.number().int().positive().optional(),
  limit: z8.coerce.number().int().positive().optional()
});

// src/rate.ts
import z9 from "zod";
var BaseHourlyRateSchema = z9.array(z9.coerce.number()).max(2);
var HourlyRateSchema = BaseHourlyRateSchema.superRefine((data, ctx) => {
  if (data.length !== 2) {
    return;
  }
  const [min, max] = data;
  if (max < min || max - min > 10) {
    ctx.addIssue({
      code: z9.ZodIssueCode.custom,
      message: "Hourly rate minimum and maximum difference must not be greater than $10",
      path: ["1"]
    });
  }
});

// src/jobs/trades.ts
import { z as z10 } from "zod";
var TradeType = /* @__PURE__ */ ((TradeType2) => {
  TradeType2["Employee"] = "Employee";
  TradeType2["Contractor"] = "Contractor";
  TradeType2["Both"] = "Both";
  return TradeType2;
})(TradeType || {});
var TradeSchema = z10.object({
  id: z10.string(),
  name: z10.string().min(1),
  description: z10.string().min(1),
  type: z10.nativeEnum(TradeType)
});
var TradesRequestSchema = z10.object({
  type: z10.nativeEnum(TradeType).optional(),
  name: z10.string().optional()
});
var TradesArraySchema = z10.array(z10.string()).min(1);
var TradesResponseSchema = z10.object({
  trades: z10.array(TradeSchema)
});

// src/jobs/job.ts
var descriptionSchema = z11.string().min(1).max(500);
var skillsSchema = z11.string().min(1).max(500);
var tradesSchema = z11.array(z11.string()).min(1);
var BaseJobSchema = z11.object({
  title: z11.string().min(1),
  description: descriptionSchema,
  address: AddressSchema,
  startDate: z11.string().datetime(),
  endDate: z11.string().datetime(),
  startTime: z11.string().datetime(),
  endTime: z11.string().datetime(),
  hourlyRate: HourlyRateSchema,
  skills: skillsSchema,
  trades: tradesSchema
});
var dateRefinement = (data, ctx) => {
  if (!data.startDate || !data.endDate)
    return;
  if (isBefore(new Date(data.endDate), new Date(data.startDate))) {
    ctx.addIssue({
      code: z11.ZodIssueCode.custom,
      message: "End date must be equal or after start date",
      path: ["endDate"]
    });
  }
};
var CreateJobRequestSchema = BaseJobSchema.extend({}).superRefine(dateRefinement);
var JobSchema = BaseJobSchema.extend({
  id: ObjectIdSchema,
  contractor: z11.string(),
  isApplied: z11.boolean().optional(),
  completed: z11.boolean(),
  image: FileSchema.optional()
}).superRefine(dateRefinement);
var JobRequestSchema = z11.object({
  id: ObjectIdSchema
});
var JobResponseSchema = z11.object({
  job: JobSchema
});
var DeleteJobRequestSchema = z11.object({
  id: ObjectIdSchema
});
var JobSchemaFilter = z11.object({
  title: z11.string().optional(),
  startDate: z11.string().datetime().optional(),
  endDate: z11.string().datetime().optional(),
  trades: TradesArraySchema.optional(),
  startTime: z11.string().datetime().optional(),
  endTime: z11.string().datetime().optional(),
  hourlyRate: BaseHourlyRateSchema.optional(),
  miles: z11.array(z11.coerce.number()).max(2).optional()
});
var JobsRequestSchema = PaginationRequestSchema.extend({
  filter: JobSchemaFilter.optional()
});
var JobsResponseSchema = withPaginationResponse(JobSchema);
var CreateJobResponseSchema = z11.object({
  job: JobSchema
});
var JobsMeRequestSchema = PaginationRequestSchema.extend({
  completed: StringBooleanSchema.optional(),
  filter: JobSchemaFilter.optional()
});
var RecommendedEmployeeSchema = z11.object({
  job: JobSchema,
  employee: EmployeeSchema
});
var UpdateJobRequestSchema = z11.object({
  id: ObjectIdSchema,
  title: z11.string().min(1).optional(),
  description: descriptionSchema.optional(),
  address: AddressSchema.optional(),
  startDate: z11.string().datetime().optional(),
  endDate: z11.string().datetime().optional(),
  startTime: z11.string().datetime().optional(),
  endTime: z11.string().datetime().optional(),
  hourlyRate: HourlyRateSchema.optional(),
  skills: skillsSchema.optional(),
  trades: tradesSchema.optional()
}).superRefine(dateRefinement);
var RecommendedJobsRequestSchema = PaginationRequestSchema.extend({});
var RecommendedJobsResponseSchema = withPaginationResponse(RecommendedEmployeeSchema);
var UpdateJobResponseSchema = z11.object({
  job: JobSchema
});
var JobsMeResponseSchema = withPaginationResponse(JobSchema);

// src/account/employee.ts
var EmployeeExperience = /* @__PURE__ */ ((EmployeeExperience2) => {
  EmployeeExperience2["LessThan1Year"] = "<1";
  EmployeeExperience2["OneToTwoYears"] = "1-2";
  EmployeeExperience2["TwoToFiveYears"] = "2-5";
  EmployeeExperience2["Over5Years"] = "5+";
  return EmployeeExperience2;
})(EmployeeExperience || {});
var ExperienceSchema = z12.nativeEnum(EmployeeExperience);
var BaseEmployeeSchema = z12.object({
  experience: ExperienceSchema,
  about: z12.string().optional(),
  trades: TradesArraySchema,
  skills: z12.string(),
  hourlyRate: HourlyRateSchema,
  address: AddressSchema,
  mobileNumber: MobileNumberSchema
});
var CreateEmployeeRequestSchema = BaseEmployeeSchema.extend({
  fileId: ObjectIdSchema.optional(),
  resumeFileId: z12.string().optional().refine((val) => val === void 0 || val === "" || isObjectID(val), {
    message: "Expected ObjectId or empty string"
  }),
  references: z12.string()
});
var EmployeeSchema = BaseEmployeeSchema.extend({
  id: ObjectIdSchema,
  firstName: z12.string(),
  lastName: z12.string(),
  email: z12.string().email(),
  profileImage: ImageSchema.optional(),
  resume: FileSchema.optional(),
  rating: z12.number().optional(),
  absent: z12.number().optional(),
  references: z12.string().optional()
});
var CreateEmployeeResponseSchema = z12.object({
  employee: EmployeeSchema
});
var UpdateEmployeeMeRequestSchema = z12.object({
  experience: ExperienceSchema.optional(),
  about: z12.string().optional(),
  trades: TradesArraySchema.optional(),
  skills: z12.string().optional(),
  hourlyRate: HourlyRateSchema.optional(),
  address: AddressSchema.optional(),
  mobileNumber: MobileNumberSchema.optional(),
  firstName: z12.string().optional(),
  lastName: z12.string().optional(),
  fileId: ObjectIdSchema.optional(),
  resumeFileId: ObjectIdSchema.optional(),
  references: z12.string().optional()
});
var UpdateEmployeeMeResponseSchema = z12.object({
  employee: EmployeeSchema
});
var EmployeesRequestSchema = PageParamsSchema.extend({});
var EmployeesResponseSchema = z12.object({
  employees: z12.array(EmployeeSchema),
  count: z12.number()
});
var EmployeeRequestSchema = z12.object({
  id: ObjectIdSchema
});
var EmployeeMeResponseSchema = z12.object({
  employee: EmployeeSchema.or(z12.null())
});
var EmployeeResponseSchema = z12.object({
  employee: EmployeeSchema
});
var DeleteEmployeeSchema = z12.object({
  id: ObjectIdSchema
});
var EmployeeJobsRequestSchema = PaginationRequestSchema.extend({});
var EmployeeJobsResponseSchema = withPaginationResponse(JobSchema);
var RecommendedEmployeesRequestSchema = PaginationRequestSchema.extend({});
var RecommendedEmployeesResponseSchema = withPaginationResponse(EmployeeSchema);

// src/account/contractor.ts
import z13 from "zod";
var CompanySize = /* @__PURE__ */ ((CompanySize2) => {
  CompanySize2["Ten"] = "1-10";
  CompanySize2["Fifty"] = "11-50";
  CompanySize2["TwoHundred"] = "51-200";
  CompanySize2["FiveHundred"] = "201-500";
  CompanySize2["Thousand"] = "501-1000";
  CompanySize2["FiveThousand"] = "1001-5000";
  CompanySize2["TenThousand"] = "5001-10000";
  CompanySize2["OverTenThousand"] = "10001+";
  return CompanySize2;
})(CompanySize || {});
var LicenseNumberSchema = z13.string().regex(/^[a-zA-Z0-9]+$/).min(1);
var BaseContractorLicenseSchema = z13.object({
  state: z13.string(),
  number: LicenseNumberSchema.optional()
});
var CreateContractorLicenseSchema = BaseContractorLicenseSchema.extend({
  fileId: ObjectIdSchema.optional()
});
var ContractorLicenseSchema = BaseContractorLicenseSchema.extend({
  file: FileSchema.optional()
});
var CreateContractorRequestSchema = z13.object({
  trades: TradesArraySchema,
  size: z13.nativeEnum(CompanySize).optional(),
  profileImageFileId: ObjectIdSchema.optional(),
  licenses: z13.array(CreateContractorLicenseSchema).min(1)
});
var ContractorSchema = CreateContractorRequestSchema.extend({
  id: ObjectIdSchema,
  name: z13.string(),
  email: z13.string().email(),
  licenses: z13.array(ContractorLicenseSchema),
  profileImage: ImageSchema.optional()
});
var UpdateContractorMeRequestSchema = z13.object({
  trades: TradesArraySchema.optional(),
  size: z13.nativeEnum(CompanySize).optional(),
  licenses: z13.array(CreateContractorLicenseSchema).optional(),
  name: z13.string().optional(),
  profileImageFileId: ObjectIdSchema.optional()
});
var CreateContractorResponseSchema = z13.object({
  contractor: ContractorSchema
});
var ContractorsRequestSchema = PageParamsSchema.extend({});
var ContractorsResponseSchema = z13.object({
  contractors: z13.array(ContractorSchema),
  count: z13.number()
});
var ContractorRequestSchema = z13.object({
  id: ObjectIdSchema
});
var ContractorResponseSchema = z13.object({
  contractor: ContractorSchema
});
var ContractorMeResponseSchema = z13.object({
  contractor: ContractorSchema.or(z13.null())
});
var DeleteContractorSchema = z13.object({
  id: ObjectIdSchema
});
var UpdateContractorMeResponseSchema = CreateContractorResponseSchema.extend({});

// src/account/review.ts
import z14 from "zod";
var QuestionSchema = z14.object({
  title: z14.string(),
  rating: z14.number(),
  answer: z14.boolean()
});
var CreateEmployeeReviewRequestSchema = z14.object({
  id: ObjectIdSchema,
  jobId: ObjectIdSchema,
  isAbsent: z14.boolean(),
  questions: z14.array(QuestionSchema),
  applicationId: ObjectIdSchema
});
var ReviewSchema = CreateEmployeeReviewRequestSchema.extend({
  contractorId: ObjectIdSchema,
  contractor: ContractorSchema,
  employeeId: ObjectIdSchema,
  employee: EmployeeSchema,
  applicationId: ObjectIdSchema,
  job: JobSchema,
  createdAt: z14.string().datetime(),
  rating: z14.number()
});
var CreateEmployeeReviewResponseSchema = z14.object({
  review: ReviewSchema
});
var GetEmployeeReviewsRequestSchema = PaginationRequestSchema.extend({
  id: ObjectIdSchema
});
var GetEmployeeReviewsResponseSchema = withPaginationResponse(ReviewSchema);

// src/jobs/application.ts
import { z as z15 } from "zod";
var ApplyJobRequestSchema = z15.object({
  id: ObjectIdSchema
});
var ApplicationStatus = /* @__PURE__ */ ((ApplicationStatus2) => {
  ApplicationStatus2["AwaitingResponse"] = "Awaiting Response";
  ApplicationStatus2["Accepted"] = "Accepted";
  ApplicationStatus2["Rejected"] = "Rejected";
  ApplicationStatus2["Completed"] = "Completed";
  return ApplicationStatus2;
})(ApplicationStatus || {});
var ApplicationSchema = z15.object({
  id: ObjectIdSchema,
  status: z15.nativeEnum(ApplicationStatus),
  employee: EmployeeSchema,
  job: JobSchema,
  contractor: ContractorSchema,
  isReviewed: z15.boolean().optional()
});
var ApplicationsSchema = z15.object({
  applications: z15.array(ApplicationSchema)
});
var ContractorApplicationsRequestSchema = PaginationRequestSchema.extend({});
var ContractorApplicationsResponseSchema = withPaginationResponse(ApplicationsSchema);
var EmployeeApplicationsRequestSchema = PaginationRequestSchema.extend({});
var EmployeeApplicationsResponseSchema = withPaginationResponse(ApplicationsSchema);
var ApplyJobResponseSchema = z15.object({
  application: ApplicationSchema
});
var JobApplicationsRequestSchema = PaginationRequestSchema.extend({
  id: ObjectIdSchema
});
var AcceptApplicationResponseSchema = z15.object({
  application: ApplicationSchema
});
var AcceptApplicationRequestSchema = z15.object({
  id: ObjectIdSchema
});
var DeclineApplicationRequestSchema = z15.object({
  id: ObjectIdSchema
});
var DeclineApplicationResponseSchema = z15.object({
  application: ApplicationSchema
});
var JobApplicationsResponseSchema = withPaginationResponse(ApplicationsSchema);
var ApplicationRequestSchema = z15.object({
  id: ObjectIdSchema
});
var ApplicationResponseSchema = z15.object({
  application: ApplicationSchema
});

// src/stats/stats.ts
import z16 from "zod";
var StatsFilter = /* @__PURE__ */ ((StatsFilter2) => {
  StatsFilter2["ThisWeek"] = "This week";
  StatsFilter2["ThisYear"] = "This year";
  StatsFilter2["ThisMonth"] = "This month";
  return StatsFilter2;
})(StatsFilter || {});
var StatsRequestSchema = z16.object({
  filter: z16.nativeEnum(StatsFilter)
});
var StatsResponseSchema = z16.object({
  users: z16.number(),
  employees: z16.number(),
  employers: z16.number(),
  jobs: z16.number()
});

// src/notifications/notification.ts
import z17 from "zod";
var NotificationType = /* @__PURE__ */ ((NotificationType2) => {
  NotificationType2["NewApplicant"] = "NEW_APPLICANT";
  NotificationType2["EmployeeReview"] = "EMPLOYEE_REVIEW";
  NotificationType2["NewJob"] = "NEW_JOB";
  NotificationType2["ApplicationResponse"] = "APPLICATION_RESPONSE";
  return NotificationType2;
})(NotificationType || {});
var NotificationPayloadSchema = z17.object({
  type: z17.nativeEnum(NotificationType),
  id: z17.string()
});
var NotificationSchema = z17.object({
  id: z17.string(),
  title: z17.string(),
  message: z17.string(),
  image: FileSchema.optional(),
  readAt: z17.string().datetime().optional(),
  createdAt: z17.string().datetime(),
  payload: NotificationPayloadSchema
});
var NotificationsRequestSchema = PaginationRequestSchema.extend({});
var NotificationsResponseSchema = withPaginationResponse(NotificationSchema);
var ReadNotificationRequestSchema = z17.object({
  id: z17.string()
});
var ReadNotificationResponseSchema = z17.object({
  notification: NotificationSchema
});
var GetNotificationsCountSchema = z17.object({
  count: z17.number()
});
export {
  AcceptApplicationRequestSchema,
  AcceptApplicationResponseSchema,
  AddressSchema,
  AdminLoginRequestSchema,
  AdminLoginResponseSchema,
  ApplicationRequestSchema,
  ApplicationResponseSchema,
  ApplicationSchema,
  ApplicationStatus,
  ApplyJobRequestSchema,
  ApplyJobResponseSchema,
  BaseContractorLicenseSchema,
  ChangePasswordRecoveryCodeRequestSchema,
  ChangePasswordRecoveryRequestSchema,
  ChangePasswordSchema,
  CodeType,
  CompanySize,
  ContractorApplicationsRequestSchema,
  ContractorApplicationsResponseSchema,
  ContractorLicenseSchema,
  ContractorMeResponseSchema,
  ContractorRequestSchema,
  ContractorResponseSchema,
  ContractorSchema,
  ContractorsRequestSchema,
  ContractorsResponseSchema,
  CreateContractorRequestSchema,
  CreateContractorResponseSchema,
  CreateEmployeeRequestSchema,
  CreateEmployeeReviewRequestSchema,
  CreateEmployeeReviewResponseSchema,
  CreateFileRequestSchema,
  CreateFileResponseSchema,
  CreateJobRequestSchema,
  CreateJobResponseSchema,
  DeclineApplicationRequestSchema,
  DeclineApplicationResponseSchema,
  DeleteContractorSchema,
  DeleteEmployeeSchema,
  DeleteJobRequestSchema,
  EmployeeApplicationsRequestSchema,
  EmployeeApplicationsResponseSchema,
  EmployeeExperience,
  EmployeeJobsRequestSchema,
  EmployeeJobsResponseSchema,
  EmployeeMeResponseSchema,
  EmployeeRequestSchema,
  EmployeeResponseSchema,
  EmployeeSchema,
  EmployeesRequestSchema,
  EmployeesResponseSchema,
  FileSchema,
  FileState,
  GetEmployeeReviewsRequestSchema,
  GetEmployeeReviewsResponseSchema,
  GetFileRequestSchema,
  GetFileResponseSchema,
  GetNotificationsCountSchema,
  GetUploadResponseSchema,
  JobApplicationsRequestSchema,
  JobApplicationsResponseSchema,
  JobRequestSchema,
  JobResponseSchema,
  JobSchema,
  JobsMeRequestSchema,
  JobsMeResponseSchema,
  JobsRequestSchema,
  JobsResponseSchema,
  LicenseNumberSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  MobileNumberSchema,
  NotificationPayloadSchema,
  NotificationSchema,
  NotificationType,
  NotificationsRequestSchema,
  NotificationsResponseSchema,
  PaginationRequestSchema,
  ReadNotificationRequestSchema,
  ReadNotificationResponseSchema,
  RecommendedEmployeeSchema,
  RecommendedEmployeesRequestSchema,
  RecommendedEmployeesResponseSchema,
  RecommendedJobsRequestSchema,
  RecommendedJobsResponseSchema,
  RefreshTokenRequestSchema,
  RequestPasswordRecoveryCodeRequestSchema,
  RequestSignUpCodeRequestSchema,
  ReviewSchema,
  SignUpRequestSchema,
  SignUpResponseSchema,
  StatsFilter,
  StatsRequestSchema,
  StatsResponseSchema,
  StringBooleanSchema,
  TradeSchema,
  TradeType,
  TradesRequestSchema,
  TradesResponseSchema,
  UpdateContractorMeRequestSchema,
  UpdateContractorMeResponseSchema,
  UpdateEmployeeMeRequestSchema,
  UpdateEmployeeMeResponseSchema,
  UpdateJobRequestSchema,
  UpdateJobResponseSchema,
  UploadFieldsSchema,
  UserRole,
  UserSchema,
  VerifyPasswordRecoveryCodeRequestSchema,
  withPaginationResponse
};
//# sourceMappingURL=index.mjs.map